// src/services/auth.service.ts
import axios, { AxiosInstance } from 'axios';
import { LoginRequest, RegisterRequest, AuthResponse, User } from '../types';
import { installAuthInterceptor } from './auth-interceptor';

class AuthService {
    private api: AxiosInstance;
    private tokenKey = 'access_token';
    private refreshTokenKey = 'refresh_token';
    private userKey = 'user_data';
    private refreshPromise: Promise<string | null> | null = null;

    constructor() {
        this.api = axios.create({
            baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, ''),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Interceptor để thêm token vào request
        this.api.interceptors.request.use((config) => {
            const token = this.getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });

        installAuthInterceptor(this.api, this);
    }

    async login(credentials: LoginRequest): Promise<AuthResponse> {
        try {
            const response = await this.api.post<any>('/auth/login', credentials);

            const { accessToken, refreshToken } = response.data;

            if (accessToken) localStorage.setItem('access_token', accessToken);
            if (refreshToken) localStorage.setItem('refresh_token', refreshToken);


            let user: User | null = null;

            // 1) Try response data first
            const payloadUser = response.data?.user || response.data?.data?.user || response.data?.data;
            if (payloadUser && payloadUser._id) {
                user = payloadUser as User;
            }

            // 2) Fallback to /users/me
            if (!user) {
                try {
                    const userResponse = await this.api.get<any>('/users/me', {
                        headers: { Authorization: `Bearer ${accessToken}` }
                    });
                    const userData = userResponse.data;
                    user = (userData && 'data' in userData ? userData.data : userData) as User;
                } catch (getUserError) {
                    console.warn('⚠️ Failed to get user data from /users/me:', getUserError);
                }
            }

            if (user) {
                localStorage.setItem('user_data', JSON.stringify(user));
            }

            return {
                accessToken,
                refreshToken,
                user: user || null,
            };
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message || 'Login failed');
        }
    }

    async register(userData: RegisterRequest): Promise<AuthResponse> {
        try {
            const response = await this.api.post<AuthResponse>('/auth/register', userData);
            const { accessToken, refreshToken, user } = response.data;
            if (!accessToken || !refreshToken || !user?._id) {
                throw new Error('API đăng ký chưa trả đủ thông tin để đăng nhập tự động.');
            }

            this.setTokens(accessToken, refreshToken);
            this.setUser(user);
            return {
                accessToken,
                refreshToken,
                user,
            };
        } catch (error: any) {
            if (error instanceof Error && !axios.isAxiosError(error)) {
                throw error;
            }
            throw new Error(error.response?.data?.message || 'Registration failed');
        }
    }

    refreshToken(): Promise<string | null> {
        if (this.refreshPromise) return this.refreshPromise;
        const accessToken = this.getAccessToken();
        const refreshToken = this.getRefreshToken();
        if (!accessToken || !refreshToken) {
            this.logout();
            return Promise.resolve(null);
        }
        const isCurrentSession = () => this.getAccessToken() === accessToken && this.getRefreshToken() === refreshToken;
        const request: Promise<string | null> = this.api.post<{ accessToken: string; refreshToken: string }>(
            '/auth/refresh_token',
            { accessToken, refreshToken },
            { timeout: 15000 },
        ).then((response) => {
                if (!isCurrentSession()) return this.getAccessToken();
                const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
                if (!newAccessToken || !newRefreshToken) throw new Error('API chưa trả đủ thông tin phiên đăng nhập.');
                this.setTokens(newAccessToken, newRefreshToken);
                return newAccessToken;
            }).catch((error) => {
                if (!isCurrentSession()) return this.getAccessToken();
                if ([400, 401, 403, 404].includes(error.response?.status)) {
                    this.logout();
                    return null;
                }
                throw error;
            }).finally(() => {
                if (this.refreshPromise === request) this.refreshPromise = null;
            });
        this.refreshPromise = request;
        return request;
    }

    async verifyToken(token: string): Promise<boolean> {
        try {
            await this.api.get('/users/me', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return true;
        } catch (error) {
            return false;
        }
    }

    async getCurrentUser(): Promise<User | null> {
        try {
            const response = await this.api.get<User>('/users/me');
            const user = response.data;
            this.setUser(user);
            return user;
        } catch (error) {
            return null;
        }
    }

    logout(): void {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.refreshTokenKey);
        localStorage.removeItem(this.userKey);
        window.dispatchEvent(new Event('auth:logout'));
    }

    private setTokens(accessToken: string, refreshToken: string): void {
        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem(this.refreshTokenKey, refreshToken);
        window.dispatchEvent(new Event('auth:tokens-updated'));
    }

    getAccessToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(this.refreshTokenKey);
    }

    private setUser(user: User): void {
        localStorage.setItem(this.userKey, JSON.stringify(user));
    }

    getUser(): User | null {
        const data = localStorage.getItem(this.userKey);
        if (data) {
            try {
                return JSON.parse(data);
            } catch {
                return null;
            }
        }
        return null;
    }

    isAuthenticated(): boolean {
        return !!this.getAccessToken() && !!this.getUser();
    }
}

export const authService = new AuthService();
